/**
 * Route-level error boundary.
 *
 * Catches render-time exceptions in any descendant tree and renders a
 * friendly fallback instead of unmounting the whole app.
 *
 *   <ErrorBoundary onError={(e, info) => log(e)}>
 *     <Routes>...</Routes>
 *   </ErrorBoundary>
 */
import React from "react";
import Box from "@mui/material/Box";
import EmptyState from "./EmptyState";

export class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { error: null };
    }

    static getDerivedStateFromError(error) {
        return { error };
    }

    componentDidCatch(error, info) {
        if (typeof this.props.onError === "function") {
            try {
                this.props.onError(error, info);
            } catch {
                /* swallow */
            }
        }
        // eslint-disable-next-line no-console
        console.error("[ErrorBoundary]", error, info);
    }

    handleReset = () => {
        this.setState({ error: null });
    };

    handleReload = () => {
        if (typeof window !== "undefined") {
            window.location.reload();
        }
    };

    render() {
        const { error } = this.state;
        const { fallback, children } = this.props;
        if (!error) return children;
        if (fallback) return fallback(error, { reset: this.handleReset, reload: this.handleReload });

        return (
            <Box sx={{ p: 6 }}>
                <EmptyState
                    illustration="error"
                    title="Something went wrong"
                    description={
                        error?.message ||
                        "An unexpected error occurred while rendering this page."
                    }
                    action={
                        <button
                            type="button"
                            onClick={this.handleReload}
                            style={{
                                background: "var(--pt-primary)",
                                color: "#fff",
                                border: "none",
                                borderRadius: 8,
                                padding: "10px 18px",
                                fontWeight: 600,
                                cursor: "pointer",
                            }}
                        >
                            Reload page
                        </button>
                    }
                />
            </Box>
        );
    }
}

export default ErrorBoundary;
